import { Component, ElementRef, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { PlatformService } from 'src/app/api/platform.service';

export type SupportedInputTypes = string | number | boolean;

let uid = 0;
export const useUniqueId = (prefix?: string) => `${prefix ?? ''}${uid++}`;

@Component({
  template: '',
})
export abstract class FormInputComponent<TModelValue extends SupportedInputTypes> implements OnInit {
  private autoId = useUniqueId('form-input-');
  public get formId(): string {
    return this.name ?? this.autoId;
  }
  @Input() label: string;
  @Input() type?: TModelValue extends string
    ? 'text' | 'email' | 'password' | 'textarea' | 'color'
    : TModelValue extends number
    ? 'number' | 'range'
    : TModelValue extends boolean
    ? 'checkbox' | 'switch'
    : never;
  @Input() max: TModelValue extends number ? number : undefined;
  @Input() min: TModelValue extends number ? number : undefined;
  @Input() step: TModelValue extends number ? number : undefined;
  @Input() maxLength: TModelValue extends number ? number : undefined;
  @Input() rows: string;
  @Input() required = false;
  @Input() disabled = false;
  @Input() value: TModelValue;
  @Input() autocomplete?:
    | 'on'
    | 'off'
    | 'name'
    | 'honorific-prefix'
    | 'given-name'
    | 'additional-name'
    | 'family-name'
    | 'honorific-suffix'
    | 'nickname'
    | 'email'
    | 'username'
    | 'new-password'
    | 'current-password'
    | 'one-time-code'
    | 'organization-title'
    | 'organization'
    | 'street-address'
    | 'address-line1'
    | 'address-line2'
    | 'address-line3'
    | 'address-level4'
    | 'address-level3'
    | 'address-level2'
    | 'address-level1'
    | 'country'
    | 'country-name'
    | 'postal-code'
    | 'cc-name'
    | 'cc-given-name'
    | 'cc-additional-name'
    | 'cc-family-name'
    | 'cc-number'
    | 'cc-exp'
    | 'cc-exp-month'
    | 'cc-exp-year'
    | 'cc-csc'
    | 'cc-type'
    | 'transaction-currency'
    | 'transaction-amount'
    | 'language'
    | 'bday'
    | 'bday-day'
    | 'bday-month'
    | 'bday-year'
    | 'sex'
    | 'tel'
    | 'tel-country-code'
    | 'tel-national'
    | 'tel-area-code'
    | 'tel-local'
    | 'tel-local-prefix'
    | 'tel-local-suffix'
    | 'tel-extension'
    | 'impp'
    | 'url'
    | 'photo';
  @Input() hasError = false;
  @Input() name?: string;
  @Input() size?: 'normal' | 'small';
  @Output() valueChange = new EventEmitter<TModelValue>();
  @Output() Focus = new EventEmitter<FocusEvent>();
  @Output() Blur = new EventEmitter<Event>();
  public showPassword = false;

  constructor(private elRef: ElementRef, public platform: PlatformService) {}

  public get inputType(): string {
    switch (typeof this.value) {
      case 'string':
        return this.type === 'password'
                ? this.showPassword
                  ? 'text' : 'password'
                : this.type === 'email'
                  ? 'email'
                  : this.type === 'textarea'
                    ? 'textarea'
                    : this.type === 'color'
                      ? 'color'
                      : 'text';
      case 'number':
        return this.type === 'range' ? 'range' : 'number';
      case 'boolean':
        return 'checkbox';
      default:
        return 'text';
    }
  }
  public get ngType(): string {
    return this.type;
  }
  public get inputProxy(): TModelValue {
    return this.value;
  }
  public set inputProxy(value: TModelValue) {
    if (this.inputType === 'checkbox' || this.inputType === 'switch') { return; }
    if (this.inputType === 'range' || this.inputType === 'number') {
      if (value === '' || isNaN(value as number)) {
        this.valueChange.emit(NaN as TModelValue);
      } else {
        this.valueChange.emit(Number(value) as TModelValue);
      }
    } else {
      this.valueChange.emit(value as TModelValue);
    }
  }

  public get isEmpty(): boolean {
    return (
      this.inputType !== 'checkbox' && (this.value === null || this.value === undefined || this.value === '' || (typeof this.value === 'number' && isNaN(this.value as number)))
    );
  }

  onChange($event: any) {
    if (this.inputType === 'checkbox' || this.inputType === 'switch') {
      this.valueChange.emit($event.target.checked as TModelValue);
    }
  }

  ngOnInit(): void {}

  public focus() {
    this.elRef.nativeElement.querySelector('input')?.focus();
    this.elRef.nativeElement.querySelector('textarea')?.focus();
  }
}

@Component({
  selector: 'app-string-input, app-form-input[type=text], app-form-input[type=email], app-form-input[type=password], app-form-input[type=textarea], app-form-input[type=color]',
  templateUrl: './form-input.component.html',
  styleUrls: ['./form-input.component.scss'],
})
export class StringFormInputComponent extends FormInputComponent<string> {}

@Component({
  selector: 'app-number-input, app-form-input[type=number], app-form-input[type=range]',
  templateUrl: './form-input.component.html',
  styleUrls: ['./form-input.component.scss'],
})
export class NumberFormInputComponent extends FormInputComponent<number> {}

@Component({
  selector: 'app-boolean-input, app-form-input[type=checkbox], app-form-input[type=switch]',
  templateUrl: './form-input.component.html',
  styleUrls: ['./form-input.component.scss'],
})
export class BooleanFormInputComponent extends FormInputComponent<boolean> {}
